import { Link } from 'react-router-dom';

import { ContactForm } from '../components/contact-form';

export default function Dashboards() {
  return (
    <div>
      <section className="relative overflow-hidden py-16" style={{ background: "var(--gradient-hero)" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>

        <div className="container-ivalue relative">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            Dashboards & Metrics
          </div>

          <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-[-0.03em] text-text-primary md:text-6xl">
            Evidence-first dashboards
            <br />
            for continuous assurance
          </h1>

          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-text-secondary">
            Templates designed for Board visibility, CISO operations, and audit readiness — powered by evidence automation.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/technology-mapping"
              className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[#10B981] to-[#059669] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              View technology mapping →
            </Link>
            <Link
              to="/assessment"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-5 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
            >
              Start assessment
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-background-primary py-20">
        <div className="container-ivalue">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="glass-card p-6">
              <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                Board-level executive dashboard
              </div>
              <div className="mt-3 text-xl font-semibold text-text-primary">
                Executive Compliance Dashboard
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Overall compliance
                  </div>
                  <div className="mt-2 font-[family:var(--font-space-grotesk)] text-4xl font-semibold text-text-primary">
                    78%
                  </div>
                  <div className="mt-2 text-sm text-text-secondary">↑ 5% vs Q3</div>
                </div>
                <div className="rounded-2xl border border-border-subtle bg-white/5 p-5">
                  <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Framework status
                  </div>
                  <div className="mt-3 space-y-2">
                    {[
                      { k: "RBI", v: "82%" },
                      { k: "SEBI", v: "71%" },
                      { k: "CERT-In", v: "95%" },
                      { k: "ISO", v: "85%" },
                    ].map((x) => (
                      <div key={x.k} className="flex items-center justify-between rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm">
                        <span className="font-semibold text-text-primary">{x.k}</span>
                        <span className="text-text-secondary">{x.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-border-subtle bg-white/5 p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Top 5 critical gaps
                </div>
                <div className="mt-3 space-y-2">
                  {[
                    "PAM implementation incomplete (RBI-IAM-03) — Due: 30 days",
                    "SOC 24x7 coverage gap (RBI-SOC-01) — Due: 45 days",
                    "DR drill not conducted (RBI-BCP-05) — Due: 60 days",
                    "Vendor assessments pending (RBI-TPR-01) — Due: 90 days",
                    "Log retention <5 years (RBI-SOC-03) — Due: 120 days",
                  ].map((x) => (
                    <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {x}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-border-subtle bg-white/5 p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Compliance trend (12 months)
                </div>
                <div className="mt-3 text-sm text-text-secondary">
                  Trend chart placeholder (Part 5 can use Recharts with sample series and tooltips).
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  CISO operational dashboard
                </div>
                <div className="mt-3 text-xl font-semibold text-text-primary">
                  Operations view
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    "Control effectiveness scores by domain",
                    "Evidence completeness tracker",
                    "Upcoming audit timeline",
                    "Open remediation items with aging",
                    "Vendor compliance status",
                  ].map((x) => (
                    <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {x}
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Audit-ready evidence dashboard
                </div>
                <div className="mt-3 text-xl font-semibold text-text-primary">
                  Evidence pipeline view
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    "Evidence collection progress by control",
                    "Document version tracking",
                    "Test completion status",
                    "Finding remediation tracker",
                    "Regulator submission status",
                  ].map((x) => (
                    <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {x}
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Incident & resilience dashboard
                </div>
                <div className="mt-3 text-xl font-semibold text-text-primary">
                  Resilience view
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    "MTTD/MTTR metrics with trends",
                    "Incident volume by type",
                    "DR test results and RTO achievement",
                    "Threat intelligence feed status",
                    "CERT-In reporting compliance",
                  ].map((x) => (
                    <div key={x} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {x}
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Next step
                </div>
                <div className="mt-3 text-sm text-text-secondary">
                  Connect dashboards to controls and evidence generation via the technology mapping engine.
                </div>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/technology-mapping"
                    className="focus-ring inline-flex h-11 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
                  >
                    Open mapping hub →
                  </Link>
                  <Link
                    to="/contact#contact-form"
                    className="focus-ring inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] px-4 text-sm font-semibold text-white hover:opacity-95"
                  >
                    Talk to iValue
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 glass-card p-6" id="contact-form">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              Get a live dashboard demo
            </div>
            <div className="mt-3 text-sm text-text-secondary">
              Share your frameworks and we will recommend the right dashboard template (Board, CISO ops, evidence pipeline).
            </div>
            <div className="mt-6">
              <ContactForm title="Dashboard enquiry" subtitle="We can customize templates to your frameworks and tooling." ctaLabel="Send" />
            </div>
            <div className="mt-4">
              <Link
                to="/contact#contact-form"
                className="focus-ring inline-flex h-11 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
              >
                Open full contact page →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
